<template>
  <ModalBase :show-modal="true" :esc-to-close="false" @close-modal="close" class="wizard-modal-onboarding wizard-intro" :class="{ started: started, finished: finished }" >

    <template v-slot:maincontent>
      <div class="steps-wrap wizard-intro" :class="{ 'debug': debug, 'wizard-mobile': mobileView, started: started, finished: (activeStep >= steps.length) }">

        <!-- STEPS left sidebar -->
        <div class="side-steps-wrap" ref="side-steps-wrap">
          <div class="side-steps">
            <div v-for="(step, index) in steps" :key="index" @click="activeStep = index" class="step-circle" :class="{ active: activeStep === index, complete: completedSteps.includes(index), skipped: skippedSteps.includes(index) }">
              <!-- <div class="step-circle__no" v-b-tooltip="{ trigger: 'hover', placement: 'right' }" :title="step.name"> -->
              <div class="step-circle__no">
                <span v-if="!completedSteps.includes(index) && !skippedSteps.includes(index)">{{ index + 1 }}</span>
                <AnimationItem v-if="completedSteps.includes(index)" class="check-animation" :key="index + ' ' + completedSteps.includes(index)" :animation="{ animationData: successAnimation, loop: false }" />
                <AnimationItem v-if="skippedSteps.includes(index)" class="skip-animation" :key="index + ' ' + completedSteps.includes(index)" :animation="{ animationData: pendingAnimation, loop: false }" />
              </div>
            </div>
          </div>
          <div class="side-butler" :class="{ placed: butlerPlaced && !finished }">
            <GkButler 
              :silent="false" 
              :placed="butlerPlaced"
              :final-line="finished"
              :theme="(started && !finished) ? 'brand' : 'white'" 
              :nextLine="butlerNextLine"
              :mobileView="mobileView"
              @finished-typing="placeButler"
              @lets-begin="close"
            />
          </div>
        </div>

        <transition :name="'fade'" appear mode="out-in">

          <!-- INTRO -->
          <div v-if="!started && !finished" class="intro-step" key="intro-step"></div>

          <div class="main-steps" v-if="started && activeStep < steps.length" key="main-steps">
            <transition :name="transitionName" appear mode="out-in">
              
              <!-- STEP activeStep -->
              <div class="step" :key="activeStep" :class="{ complete: completedSteps.includes(activeStep) }">

                <div class="step--content" ref="step-container">
                  <div class="step--pretitlebox" ref="step-pretitlebox">
                    {{ $gettext('Account setup') }}
                  </div>
                  <div class="step--titlebox" ref="step-titlebox">
                    <div class="step--content__title">
                      {{ steps[activeStep].title }}
                    </div>
                    <div v-if="steps[activeStep].description" class="step--content__description">
                      {{ steps[activeStep].description }}
                    </div>
                  </div>
                  
                  <div class="step--content__component" :style="{ '--wiz-component-available-height': stepComponentHeight + 'px' }">
                    <component
                      :is="steps[activeStep].component"
                      @step-completed="completeStep"
                      @step-skipped="skipStep"
                    />
                  </div>
                </div>
                
              </div>

            </transition>

          </div> <!-- steps -->

          <!-- OUTRO -->
          <div v-if="finished" class="outro-step" key="outro-step"></div>

        </transition>

      </div> <!-- steps-wrap -->
 
    </template>
  </ModalBase>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { splitFullName, capitalize } from "@/utils/";

import GekkoButton from "@/components/GekkoButton";
import ModalBase from "@/components/base_components/ModalBase";
import TitleBar from "@/components/TitleBar";
import GkButler from "@/components/wizard/components/GkButler";

import AnimationItem from '@/components/dashboard/ui/AnimationItem.vue'
import * as successAnimation from '@/css/animations/payment-complete.json';
import * as pendingAnimation from '@/css/animations/payment-pending.json';

export default {
  name: 'OnboardingWizardModal',
  components: { 
    ModalBase, 
    TitleBar, 
    AnimationItem,
    GkButler,
    GekkoButton,
    UserProfile: () => import('@/components/wizard/steps/UserProfile'),
    CompanySetup: () => import('@/components/wizard/steps/CompanySetup'),
    CompanyInfo: () => import('@/components/wizard/steps/CompanyInfo'),
    BankConnection: () => import('@/components/wizard/steps/BankConnection'),
    CompanyLogo: () => import('@/components/wizard/steps/CompanyLogo'),
  },
  data () {
    return {
      debug: false,
      showGui: false,

      successAnimation: successAnimation.default,
      pendingAnimation: pendingAnimation.default,

      transitionName: 'wizardNext',

      mobileView: false,

      started: false,
      butlerPlaced: false,

      stepComponentHeight: 0,

      activeStep: null,
      completedSteps: [],
      skippedSteps: [],
      steps: [
        {
          component: 'UserProfile',
          name: this.$gettext('User profile'),
          title: this.$gettext('Tell us about yourself'),
          butlerMessage: this.$gettext('You personally own the contents of your account. Therefore, please make sure your personal information is correct in case you want to ask questions about your account.')
        },
        {
          component: 'CompanySetup',
          name: this.$gettext('Company setup'),
          title: this.$gettext('What about your company'),
          butlerMessage: this.$gettext('Gekko provides accounting services to companies. For correct bookkeeping you therefore need to make sure that your company profile matches the information of your official company registration.')
        },
        {
          component: 'CompanyLogo',
          name: this.$gettext('Your logo'),
          title: this.$gettext('Show us your style'),
          butlerMessage: this.$gettext('For professional look, we recommend you add a company logo. If you don\'t have one, try out our complentary AI logo generator.'),
        },
        {
          component: 'CompanyInfo',
          name: this.$gettext('Other company info'),
          title: this.$gettext('Final details'),
          butlerMessage: this.$gettext('For some functions you should provide additional info. For instance the bankaccount number you would like to get paid on by your customers.')
        },
      ]
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: 'main/getCurrentCompany',
      currentUser: 'main/getCurrentUser',
    }),
    welcomeMessage () {
      let message = this.$gettext('Welcome {username}!, This wizard gives a short introduction into Gekko and allows you to setup your account. It will only take a minute or so to get started.')
      let username = ''
      if (this.currentUser.profile && this.currentUser.profile.full_name) {
        username = splitFullName(this.currentUser.profile.full_name)
        username = capitalize(username.firstName)
      }
      message = message.replace('{username}', username)
      return message
    },
    butlerNextLine () {
      let message = this.welcomeMessage
      if (this.activeStep !== null) {
        if (this.activeStep >= this.steps.length) {
          message = this.$gettext('Great! Your account is setup and now you can use Gekko as much as you like. Remember, you can use your free Basic account as much as you like, but some features are only available to Premium users.')
        } else {
          message = this.steps[this.activeStep].butlerMessage
        }
      }
      return message
    },
    finished () {
      return this.activeStep >= this.steps.length
    }
  },
  watch: {
    activeStep (newValue, oldValue) {
      let transitionDirection = (this.mobileView) ? 'Horizontal' : 'Vertical'
      this.transitionName = ((newValue > oldValue) ? 'wizardNext' : 'wizardPrev') + transitionDirection
    }
  },
  created () {
    window.addEventListener('keydown', this.closeEventListener);
    this.setupData()
  },
  mounted () {
    document.body.classList.add('modal-on')
    window.addEventListener('resize', this.handleResize);
    this.handleResize()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  destroyed () {
    window.removeEventListener('keydown', this.closeEventListener);
  },
  methods: {
    ...mapMutations({ 
      closeOnboardingWizard: 'onboarding/CLOSE_ONBOARDING_WIZARD',
    }),
    closeEventListener (event) {
      if (event.key === 'Escape') {
        // this.close()
      }
    },
    handleResize () {
      this.$nextTick(() => {
        this.mobileView = (window.innerWidth < 768)

        if (this.$refs['step-container']) {
          const stepContainerHeight = this.$refs['step-container'].offsetHeight
          this.stepComponentHeight = stepContainerHeight - this.$refs['step-titlebox'].offsetHeight - this.$refs['step-pretitlebox'].offsetHeight
          if (this.mobileView) {
            this.stepComponentHeight = this.stepComponentHeight - 60
          }
        }
      })
    },
    close () {
      if (this.completedSteps.length === this.steps.length) {
        window.mixpanel.track("Wizard: close completed")
      } else {
        window.mixpanel.track("Wizard: close incomplete", {
          "current step": this.activeStep,
          "steps completed": this.steps.join(',')
        })
      }
      document.body.classList.remove('modal-on')
      this.closeOnboardingWizard()
    },
    setupData () {
      // decide what step is active based on current user and company

      // this.started = true
      // this.butlerPlaced = true
      // this.activeStep = 2
    },
    placeButler () {
      console.log('OnboardingWizardModal::placeButler()')
      this.butlerPlaced = true

      // timeout for butler transition to bottom left
      setTimeout(() => {
        this.startWizard()
      }, 700);
    },
    startWizard () {
      this.started = true
      this.activeStep = 0
      // need to wait until main-step transition finishes 
      // and the subcomponent is mounted to calculate sizes
      setTimeout(() => {
        this.handleResize()
      }, 500);
    },
    updateStep(targetKey, removeKey) {
      const targetArray = this[targetKey]
      const removeArray = this[removeKey]
      targetArray.push(this.activeStep)
      const removeIndex = removeArray.indexOf(this.activeStep)
      if (removeIndex !== -1) {
        removeArray.splice(removeIndex, 1)
      }
    },
    completeStep () {
      window.mixpanel.track("Wizard: step completed", { "step": this.activeStep })
      this.updateStep('completedSteps', 'skippedSteps')
      this.activeStep++
    },
    skipStep () {
      window.mixpanel.track("Wizard: step skipped", { "step": this.activeStep })
      this.updateStep('skippedSteps', 'completedSteps')
      this.activeStep++
    },
  }
}
</script>

<style lang="scss">
.wizard-modal-onboarding {
  .gk-modal-backdrop {
    background: rgba(0, 0, 0, 0.7) !important;
    z-index: 1010 !important;
  }
  .gk-modal-wrapper { 
    z-index: 1011;
    .gk-modal {
      max-width: 550px !important;
      max-height: 600px;
      min-height: 600px;
      padding: 0;
      background: white;
      background-image: url('/static/img/tile_background-brand.png');
      border-radius: 16px;
      .gk-modal-content {
        .titlebar.title-modal {
          margin: 0;
          width: 100%;
          padding: 1.5rem;
          padding-bottom: 0;
          padding-top: 1rem;
          background: transparent;
          &.hidden {
            opacity: 0;
          }
          transition: opacity 0.2s ease;
          transition-delay: 0.3s;
        }
        .modal-content-wrap {
          padding: 0;
        }
        .close-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          color: white !important;
          background: black;
          width: 28px;
          height: 28px;
          padding: 0;
          border: 0px;
          &:hover { background: #e03131; }

          top: -6px;
          right: -6px;
          @media (min-width: 576px) {
            top: -10px;
            right: -10px;
          }
        }
      }
    }
  }
  &.started:not(.finished) {
    .gk-modal-wrapper {
      .gk-modal {
        background: white;
      }
    }
  }
  .anim-step {
    margin-bottom: -20px !important;
    svg {
      max-height: 200px;
    }
  }

  .wizard-modal-onboarding-form {
    width: 100%;
    form { 
      max-width: 425px;
      margin: 0 auto;
    }
    label:not(.custom-control-label) {
      font-size: 1rem;
      margin-bottom: 0.25rem;
      color: $colorTextLight;
    }
    label.custom-control-label {
      font-size: 1rem;
    }
    .custom-control {
      height: 38px;
      height: 38px;
      display: flex;
      align-items: center;
    }
    .form-control {
      border-radius: $radiusMd;
      box-shadow: none;

      &:not(textarea) {
        height: 42px;
      }
    }
    &--append {
      .form-control {
        border-right: 0;
      }
    }
    .input-group-append {
      .btn {
        border-top-right-radius: $radiusMd !important;
        border-bottom-right-radius: $radiusMd !important;
        &.disabled {
          background: $colorUnassigned;
          border-color: $colorUnassigned;
        }
      }
    }
    #dropzone-root {
      .dropzone {
        min-height: 100px;
        padding: 0rem;
        background: #F4F4F4;
        display: flex;
      }
      .dropzone-border {
        border: 0px solid #B8B8BD !important;
        border-radius: $radiusLg;
      }
      .dropzone-hint {
        color: #7A7A7A;
        justify-content: center;
        padding: 1rem;
        gap: 0.5rem;
        .dropzone-hint-text {
          font-family: DINNextLTPro-Medium, sans-serif;
          font-size: 14px;
        }
        .dropzone-hint-icon {
          color: #d6d6d6;
          margin: 0;
          pointer-events: none;
        }
      }
      .image-viewer {
        min-height: 150px;
      }
    }
  }
  .check-animation {
    overflow: visible !important;
    > svg {
      transform: scale(1.56) !important;
    }
  }
  .skip-animation {
    overflow: visible !important;
    > svg {
      transform: scale(1) !important;
    }
  }

}
</style>

<style lang="scss" scoped>
.wizard-modal-onboarding {
  $stepCircleSize: 28px;
  $stepBorderSize: 2px;
  $stepBorderColor: #EDEDED;
  $stepLinePos: calc(calc(#{$stepCircleSize} / 2) - calc(#{$stepBorderSize} / 2));
  $stepSideGap: 1.5rem;
  $mainStepWidthLg: 100%;
  $mainStepWidthSm: 100%;
  .steps-wrap.debug {
    * { outline: 1px dashed cyan; }
    outline: 1px solid black;
    .side-steps-wrap { outline: 1px solid red; }
    .main-steps { background: red; }
    .step { background: blue; }
    .step--title { background: lime; }
    .step--content { background: green; }
    .step--content__component { background: yellow; }
  }
  .steps-wrap {
    display: flex;
    height: 100%;
    width: 100%;
    margin: 0 auto;
    padding: 0 1.5rem;
  }
  .steps-wrap {
    &.wizard-intro {
      .side-steps-wrap {
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        .side-butler {
          position: absolute;
          z-index: 200;

          bottom: 50%;
          left: 20%;
          transform: scale(3);

          transition: all 0.7s ease-in-out;
          &.placed {
            bottom: 1rem;
            left: 1rem;
            transform: scale(1);
          }
        }
        @media (min-width: 768px) {
          .side-butler {
            bottom: 45%;
            left: 35%;
            transform: scale(5);
          }
        }
      }
      .side-steps, .main-steps {
        opacity: 0;
        transition: opacity 0.3s ease-in-out;
      }
      &.started:not(.finished) {
        &:not(.wizard-mobile) {
          .side-steps-wrap {
            border-right: 1px solid $colorBorderDash;
          }
        }
        .side-steps, .main-steps {
          opacity: 1;
        }
      }
    }
  }

  .side-steps-wrap {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-right: $stepSideGap;
    margin: 1.5rem 0;
    border-right: 1px solid transparent;
    transition: border-color 0.3s ease-in-out;
    .side-steps {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
      position: relative;
      &:before {
        background: #EDEDED;
        width: $stepBorderSize;
        content: '';
        height: 100%;
        position: absolute;
        top: 0;
        left: $stepLinePos;
        z-index: 1;
        opacity: 0.7;
      }
      .step-circle {
        display: flex;
        align-items: center;
        cursor: pointer;
        position: relative;
        z-index: 10;
        gap: 0.75rem;
        &__no {
          cursor: pointer;
          background: white;
          color: #c5c5c5;
          width: $stepCircleSize; 
          height: $stepCircleSize;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 100%;
          font-size: 16px;
          transition: all 0.2s ease;

          border: $stepBorderSize solid $stepBorderColor;

          span { line-height: 1; }
          z-index: 10;
        }
        &__text {
          line-height: 1;
          font-size: 15px;
          color: #bababa;
          white-space: nowrap;
        }
        &.active {
          .step-circle__no {
            animation: jump 0.6s ease;
            border: $stepBorderSize solid $colorBrand;
            background: white;
            color: $colorBrand;
          }
          .step-circle__text {
            color: $colorTextBase;
          }
        }
        &.complete, &.skipped {
          .step-circle__no {
            // animation: jump 0.6s ease;
            border: none !important;
            width: $stepCircleSize; 
            height: $stepCircleSize;
            background: white;
            .check-animation  {
              // transform: scale(1.7); // to hide animation surrounding padding
            }
            .skip-animation  {
              // transform: scale(1.1); // to hide animation surrounding padding
            }
          }
        }
        &.complete {
          .step-circle__no {
            color: $colorBrand;
          }
        }
        &.skipped {
          .step-circle__no {
            color: $colorAppHours;
          }
        }
      }
    }
  }

  .intro-step, .outro-step {
    position: absolute; 
    top: 0; 
    left: 0; 
    height: 100%; 
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
  }

  .main-steps {
    height: 100%;
    width: 100%;
    padding: 1.5rem 0;
    padding-left: 0;
    overflow: hidden;
    &--title {
      font-family: DINNextLTPro-Medium, sans-serif;
      font-size: 32px;
      padding-bottom: 0.5rem;
    }
    .step {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      position: relative;
      height: 100%;
      width: 100%;
      margin: 0 auto;
      align-items: center;
      padding: 0;
      padding-left: 1.5rem;

      &--title {
        display: flex;
        align-items: center;
        cursor: pointer;
        margin-bottom: 0.5rem;
        &__no {
          cursor: pointer;
          background: #EDEDED;
          color: #A8A8A8;
          width: 22px; 
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 100%;
          font-size: 13px;
        }
        &__text {
          line-height: 1;
          font-size: 15px;
          opacity: 0.7;
          white-space: nowrap;
        }
      }
      &--pretitlebox {
        font-size: 1rem;
        color: $colorTextLight;
        margin-bottom: 0.25rem;
      }
      &--titlebox {
        display: flex;
        flex-direction: column;
        width: $mainStepWidthSm;
      }
      &--content {
        position: relative;
        // overflow: hidden;
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        padding: 0;
        &__title {
          font-family: DINNextLTPro-Medium, sans-serif;
          font-size: 22px;
          padding-bottom: 0rem;
          line-height: 1.3;
        }
        &__description {
          color: #8F8F8F;
          font-size: 16px;
        }
        &__component {
          display: flex;
          width: $mainStepWidthSm;
          height: 100%;
          justify-content: center;
          padding-top: 1rem;
        }
      }

      @media (min-width: 768px) {
        &--titlebox {
          width: $mainStepWidthLg;
        }
        &--content {
          padding-left: 0;

          &__component {
            width: $mainStepWidthLg;
          }
        }
      }
    }
  }

  // MOBILE VIEW
  .steps-wrap.wizard-mobile {
    padding: 1rem 0rem;
    padding-bottom: 0;
    padding: 0 !important;
    flex-direction: column;
    .side-steps-wrap {
      border: none;
      margin: 0;
      padding: 1rem 0;
      order: 2;
      .side-steps {
        width: 100%;
        flex-direction: row;
        gap: 0;
        padding-left: 3rem;
        &:before { display: none; }
        .step-circle {
          flex-direction: column;
          gap: 0.75rem;
          padding: 0 0.5rem;
          flex: 1;
          &:before {
            content: '';
            width: 100%;
            height: $stepBorderSize;
            position: absolute;
            top: $stepLinePos;
            left: 0px;
            z-index: 1;
            background: $stepBorderColor;
            opacity: 0.7;
          }
          &:first-child {
            &:before {
              width: 50%;
              left: 50%;
            }
          }
          &:last-child {
            &:before {
              width: 50%;
            }
          }
          &__text {
            font-size: 15px;
            white-space: normal;
            text-align: center;
          }
        }
      }
    }
    .main-steps {
      padding: 0.5rem;
      padding-bottom: 0.25rem;
      margin: 0 auto;
      .step {
        padding: 0 0.5rem !important;
        &--content {
          padding: 0;
        }
      }
    }
  }
}


 @keyframes jump {
  0% { transform: scale(1) rotate(0deg); }
  30% { transform: scale(1.2) rotate(-15deg); }
  50% { transform: scale(1.3) rotate(15deg); }
  70% { transform: scale(1.2) rotate(-7deg); }
  100% { transform: scale(1) rotate(0deg); }
}

</style>